import { CommonModule, Location } from '@angular/common';
import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { IonicModule, LoadingController } from '@ionic/angular';
import { TranslateConfigService } from 'src/app/services/translate-config.service';
import { RefundService } from 'src/app/services/api/refund.service';
import { BlockedsService } from 'src/app/services/api/blockeds.service';
import { ToastService } from 'src/app/services/toast.service';
import { ClipboardFeedbackService } from 'src/app/services/clipboard-feedback.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { AppStorageService } from '../../../services/app-storage.service';
import { StorageKeys } from '../../../services/storage-keys';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DialogComponent } from '../../dialog/dialog.component';
import { LongPressDirective } from 'src/app/shared/directives/long-press.directive';

@Component({
  selector: 'app-chat-header',
  templateUrl: './chat-header.component.html',
  styleUrls: ['./chat-header.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, FormsModule, TranslateModule, DialogComponent, LongPressDirective]
})
export class ChatHeaderComponent implements OnInit {
  @Input() user: any;
  @Input() isWeb: boolean = false;
  @Input() isMobile: boolean = false;
  @Input() isRefunded: boolean = false;
  // Exchange / status inputs (moved from messages page)
  @Input() conversationId: any;
  @Input() exchangeStatus: any = null;
  @Input() exchangeLoading: boolean = false;
  @Input() isReceptor: boolean = false;
  @Input() isBlocked: boolean = false;
  @Input() isContactDeleted: boolean = false;
  @Input() isExchangeClosed: boolean = false;
  @Input() isPayer: boolean = false;
  @Input() isObjectOwner: boolean = false;
  @Input() premiumChatUnlocked: boolean = true;
  @Input() hideUserInfo: boolean = false;

  // Two-way bindable fields
  @Input() exchangeCodeInput: string = '';
  @Output() exchangeCodeInputChange: EventEmitter<string> =
    new EventEmitter<string>();

  // Actions delegated to parent
  @Output() generateExchangeCode: EventEmitter<void> = new EventEmitter<void>();
  @Output() validateExchangeCode: EventEmitter<any> = new EventEmitter<any>();

  avatar = '../../../../assets/images/image-holder.png';
  isGuest: boolean = false;
  show: boolean = false;

  constructor(
    private router: Router,
    private location: Location,
    private loadingCtrl: LoadingController,
    private toastService: ToastService,
    private clipboardFeedbackService: ClipboardFeedbackService,
    private refundService: RefundService,
    private blockedsService: BlockedsService,
    private utilsService: UtilsService,
    public translateConfigService: TranslateConfigService,
    private sanitizer: DomSanitizer,
    private storage: AppStorageService
  ) { }

  navigate(path: string, extras?: any) {
    this.router.navigate([path], extras);
  }

  goBack() {
    this.location.back();
  }

  toggleShow() {
    this.show = !this.show;
  }

  get canRequestRefund(): boolean {
    return this.isObjectOwner && this.isPayer && this.premiumChatUnlocked;
  }

  async report() {
    // Verificar si ya fue reembolsado
    if (this.isRefunded) {
      this.toastService.showInfo(
        this.translateConfigService.getItemTranslated(
          'CHAT.REFUND_ALREADY_PROCESSED'
        ),
        ''
      );
      return;
    }



    // Verificar si es invitado: nos basamos en el token de sesión (la credencial
    // real), no en el objeto USER, que puede no estar cargado en algunos contextos
    // y provocaba que a un usuario autenticado se le redirigiera a login.
    const token = this.storage.get(StorageKeys.TOKEN);
    this.isGuest = !token;

    if (this.isGuest) {
      // Redirigir a login o mostrar modal de invitado
      const isWeb = await this.utilsService.detectIsWeb();

      this.router.navigate([!isWeb ? '/selection' : '/login']);
    } else {
      // Solo el propietario del objeto que pagó el premium chat puede solicitar la devolución.
      if (!this.canRequestRefund) {
        await this.toastService.showError(
          this.translateConfigService.getItemTranslated('messages.only_payer_can_refund') ||
          this.translateConfigService.getItemTranslated('CHAT.REFUND_REQUEST_FORBIDDEN') ||
          'Solo quien realizó el pago puede solicitar un reembolso',
          ''
        );
        return;
      }

      // Mostrar modal de confirmación
      this.toggleShow();
    }
  }

  async requestRefund() {
    try {
      if (!this.canRequestRefund) {
        await this.toastService.showError(
          this.translateConfigService.getItemTranslated('CHAT.REFUND_REQUEST_FORBIDDEN'),
          ''
        );
        return;
      }

      // Verificar si ya fue reembolsado antes de proceder
      if (this.isRefunded) {
        await this.toastService.showInfo(
          this.translateConfigService.getItemTranslated(
            'CHAT.REFUND_ALREADY_PROCESSED'
          ),
          ''
        );
        return;
      }

      if (!this.conversationId) {
        await this.toastService.showError(
          this.translateConfigService.getItemTranslated(
            'CHAT.REFUND_REQUEST_INVALID'
          ),
          ''
        );
        return;
      }

      const loading = await this.loadingCtrl.create({
        message: this.translateConfigService.getItemTranslated(
          'CHAT.PROCESSING_REFUND_REQUEST'
        ),
      });
      await loading.present();

      this.refundService.requestPremiumChatRefund(Number(this.conversationId)).subscribe({
        next: async (response) => {
          await loading.dismiss();

          if (response.success) {
            await this.toastService.showSuccess(
              this.translateConfigService.getItemTranslated(
                'CHAT.REFUND_REQUEST_SUCCESS'
              ),
              ''
            );
            this.isRefunded = true;
          } else {
            await this.toastService.showError(
              response.message ||
              this.translateConfigService.getItemTranslated(
                'CHAT.REFUND_REQUEST_ERROR'
              ),
              ''
            );
          }
        },
        error: async (error) => {
          await loading.dismiss();

          // Manejar diferentes tipos de errores
          let errorMessage = this.translateConfigService.getItemTranslated(
            'CHAT.REFUND_REQUEST_ERROR'
          );

          if (error.status === 400) {
            errorMessage = this.translateConfigService.getItemTranslated(
              'CHAT.REFUND_REQUEST_NOT_PAID'
            );
          } else if (error.status === 403) {
            errorMessage = this.translateConfigService.getItemTranslated(
              'CHAT.REFUND_REQUEST_FORBIDDEN'
            );
          } else if (error.status === 409) {
            errorMessage = this.translateConfigService.getItemTranslated(
              'CHAT.REFUND_REQUEST_ALREADY_EXISTS'
            );
            // Si el error es que ya existe, marcar como reembolsado
            this.isRefunded = true;
          }

          await this.toastService.showError(errorMessage, '');
        },
      });
    } catch (error) {
      await this.toastService.showError(
        this.translateConfigService.getItemTranslated(
          'COMMON.UNEXPECTED_ERROR'
        ),
        ''
      );
    }
  }

  ngOnInit() {
    // Verificar estado de invitado al inicializar
    const userStorage = this.storage.getJSON<any>(StorageKeys.USER) || {};
    this.isGuest = !userStorage || !userStorage.id;
    // Prepare sanitized HTML for code dialog
    this.updateCodeDialogInfo();
    // Refresh when language changes
    try {
      this.translateConfigService.onLanguageChange().subscribe(() => {
        this.updateCodeDialogInfo();
      });
    } catch (e) { /* ignore */ }
  }

  // Safe HTML used in the code dialog (renders <strong> etc.)
  codeDialogInfoSafe: SafeHtml | string = '';

  private updateCodeDialogInfo() {
    try {
      const raw = this.translateConfigService.getItemTranslated('CHAT.CODE_DIALOG_INFO');
      if (raw !== undefined && raw !== null) {
        // Use bypassSecurityTrustHtml so Angular won't strip allowed tags
        this.codeDialogInfoSafe = this.sanitizer.bypassSecurityTrustHtml(String(raw));
      } else {
        this.codeDialogInfoSafe = '';
      }
    } catch (e) {
      this.codeDialogInfoSafe = '';
    }
  }

  // Validation dialog state and handlers
  showValidationDialog: boolean = false;
  showGenerateCodeConfirmDialog: boolean = false;
  private generateCodeDialogClosedAt: number = 0;
  // Dialogo para mostrar código generado al receptor (preview tipo screenshot)
  showCodeDialog: boolean = false;
  // Block user dialog states
  showBlockConfirm: boolean = false;
  showBlockSuccess: boolean = false;
  private navigateToBlockedUsersOnSuccess: boolean = false;
  private blocking: boolean = false;
  // Options popover
  showOptionsPopover: boolean = false;
  popoverEvent: any = null;

  openCodeDialog() {
    if (this.isReceptor && this.exchangeStatus?.status === 'code_generated') {
      this.showCodeDialog = true;
    }
  }

  toggleOptionsPopover(ev?: any) {
    this.popoverEvent = ev;
    this.showOptionsPopover = !this.showOptionsPopover;
  }
  closeCodeDialog() { this.showCodeDialog = false; }

  async copyValidationCode(): Promise<void> {
    await this.clipboardFeedbackService.copyText(
      String(this.exchangeStatus?.codeMasked ?? ''),
      this.translateConfigService.getItemTranslated('TEXT_COPIED') || 'Texto copiado al portapapeles',
      this.translateConfigService.getItemTranslated('COMMON.UNEXPECTED_ERROR') || 'An unexpected error has occurred',
    );
  }

  openValidationDialog() {
    this.showValidationDialog = true;
  }

  toggleValidationDialog() {
    this.showValidationDialog = !this.showValidationDialog;
  }

  confirmValidation() {
    // prevent empty submits
    if (!this.exchangeCodeInput) {
      return;
    }

    // Close the code input dialog and emit validation
    this.showValidationDialog = false;
    const payload = { code: this.exchangeCodeInput };
    this.validateExchangeCode.emit(payload);
    this.exchangeCodeInput = '';
    this.exchangeCodeInputChange.emit(this.exchangeCodeInput);
  }

  confirmGenerateCode() {
    this.closeGenerateCodeDialog();

    if (!this.isReceptor || this.exchangeStatus?.status === 'code_generated') {
      return;
    }

    this.generateExchangeCode.emit();
  }

  // Entry point when receptor clicks validate transaction button
  startValidationFlow() {

    if (this.showGenerateCodeConfirmDialog) {
      return;
    }

    if (this.exchangeStatus?.status === 'code_generated') {
      if (this.isReceptor) {
        this.openCodeDialog()
      }
      return;
    }

    if (Date.now() - this.generateCodeDialogClosedAt < 250) {
      return;
    }

    this.showGenerateCodeConfirmDialog = true;
  }

  closeGenerateCodeDialog() {
    this.showGenerateCodeConfirmDialog = false;
    this.generateCodeDialogClosedAt = Date.now();
  }

  // Block user flow (simplified from user-profile)
  openBlockDialog() {
    if (!this.user || !this.user.id || this.blocking) return;
    this.showBlockConfirm = true;
  }

  cancelBlock() {
    this.showBlockConfirm = false;
  }

  proceedBlock() {
    if (!this.user || !this.user.id) {
      this.toastService.showError(
        this.translateConfigService.getItemTranslated('USER_PROFILE.BLOCK_USER_ERROR'),
        ''
      );
      this.showBlockConfirm = false;
      return;
    }
    this.blocking = true;
    this.showBlockConfirm = false;

    this.blockedsService.blockUser(this.user.id).subscribe({
      next: async (response: any) => {
        this.blocking = false;
        this.navigateToBlockedUsersOnSuccess = true;
        this.showBlockSuccess = true;
      },
      error: async (error) => {
        this.blocking = false;
        let errorMessage = this.translateConfigService.getItemTranslated(
          'USER_PROFILE.BLOCK_USER_ERROR'
        );
        if (error.status === 400) {
          errorMessage = this.translateConfigService.getItemTranslated('USER_PROFILE.BLOCK_USER_INVALID');
        } else if (error.status === 403) {
          errorMessage = this.translateConfigService.getItemTranslated('USER_PROFILE.BLOCK_USER_FORBIDDEN');
        } else if (error.status === 409) {
          errorMessage = this.translateConfigService.getItemTranslated('USER_PROFILE.USER_ALREADY_BLOCKED');
          this.navigateToBlockedUsersOnSuccess = true;
          this.showBlockSuccess = true;
        }
        await this.toastService.showError(errorMessage, '');
      },
    });
  }

  closeBlockSuccess() {
    this.showBlockSuccess = false;
    this.navigateToBlockedUsersOnSuccess = false;
  }

  confirmBlockSuccess() {
    const shouldNavigate = this.navigateToBlockedUsersOnSuccess;

    this.closeBlockSuccess();

    if (shouldNavigate) {
      void this.router.navigate(['/blocked-users']);
    }
  }
}
